import { Suspense, lazy, ComponentType } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import BackToTop from "@/components/BackToTop";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import DomainRedirect from "@/components/DomainRedirect";
import { usePageTracking } from "@/hooks/usePageTracking";
import { useConversionTracking } from "@/hooks/useConversionTracking";
import { DIAGNOSES } from "@/data/diagnoses";
import { LOCATIONS } from "@/data/locations";
import { KNOWLEDGE } from "@/data/knowledge";
import { AUTHORS } from "@/data/authors";
import { COMPARISONS } from "@/data/comparisons";
import { SPORTS } from "@/data/sports";
import { DIAGNOSTIC_SERVICES } from "@/data/diagnosticServices";
import { EN_LANDINGS } from "@/data/englishLandings";
import "./i18n";

// Critical path - load immediately
import Index from "./pages/Index";

// Retry wrapper for lazy imports (handles stale chunks after deploy)
const LAZY_RELOAD_KEY = "lazy-reloaded-at";
const LAZY_RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (e: any) => {
  const msg = String(e?.message || e || "");
  return (
    /Loading chunk|Loading CSS chunk|Unable to preload|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
      msg
    )
  );
};

const lazyRetry = (fn: () => Promise<{ default: ComponentType<any> }>) =>
  lazy(() =>
    fn().catch(async (e1) => {
      // First retry after short delay (handles transient CSS/chunk preload failures)
      await new Promise((r) => setTimeout(r, 400));
      try {
        return await fn();
      } catch (e2) {
        if (isChunkLoadError(e2)) {
          const last = Number(sessionStorage.getItem(LAZY_RELOAD_KEY) || 0);
          const now = Date.now();
          if (now - last > LAZY_RELOAD_COOLDOWN_MS) {
            sessionStorage.setItem(LAZY_RELOAD_KEY, String(now));
            window.location.reload();
            return new Promise(() => {}) as any;
          }
        }
        throw e2;
      }
    })
  );

// Lazy load non-critical pages
const Services = lazyRetry(() => import("./pages/Services"));
const About = lazyRetry(() => import("./pages/About"));
const Contact = lazyRetry(() => import("./pages/Contact"));
const Booking = lazyRetry(() => import("./pages/Booking"));
const Blog = lazyRetry(() => import("./pages/Blog"));
const OnlineCoaching = lazyRetry(() => import("./pages/OnlineCoaching"));
const ClinicalEducation = lazyRetry(() => import("./pages/ClinicalEducation"));
const Diagnostics = lazyRetry(() => import("./pages/Diagnostics"));
const DiagnosticServiceLanding = lazyRetry(() => import("./pages/DiagnosticServiceLanding"));
const ChiropracticLanding = lazyRetry(() => import("./pages/ChiropracticLanding"));
const PhysioTherapyLanding = lazyRetry(() => import("./pages/PhysioTherapyLanding"));
const MassageLanding = lazyRetry(() => import("./pages/MassageLanding"));
const MassageCityLanding = lazyRetry(() => import("./pages/MassageCityLanding"));
const LaserTherapyLanding = lazyRetry(() => import("./pages/LaserTherapyLanding"));
const Pricing = lazyRetry(() => import("./pages/Pricing"));
const SymptomAdvisor = lazyRetry(() => import("./pages/SymptomAdvisor"));
const SiteSearch = lazyRetry(() => import("./pages/SiteSearch"));
const LymphaticDrainageLanding = lazyRetry(() => import("./pages/LymphaticDrainageLanding"));
const PressotherapyLanding = lazyRetry(() => import("./pages/PressotherapyLanding"));
const ShockwaveLanding = lazyRetry(() => import("./pages/ShockwaveLanding"));
const ShockwaveCityLanding = lazyRetry(() => import("./pages/ShockwaveCityLanding"));
const FatRemovalLanding = lazyRetry(() => import("./pages/FatRemovalLanding"));
const AntiCelluliteLanding = lazyRetry(() => import("./pages/AntiCelluliteLanding"));
const DiagnosisLanding = lazyRetry(() => import("./pages/DiagnosisLanding"));
const LocationLanding = lazyRetry(() => import("./pages/LocationLanding"));
const KnowledgeHub = lazyRetry(() => import("./pages/KnowledgeHub"));
const KnowledgeLanding = lazyRetry(() => import("./pages/KnowledgeLanding"));
const BelgradePillar = lazyRetry(() => import("./pages/BelgradePillar"));
const AuthorLanding = lazyRetry(() => import("./pages/AuthorLanding"));
const ComparisonLanding = lazyRetry(() => import("./pages/ComparisonLanding"));
const SportLanding = lazyRetry(() => import("./pages/SportLanding"));
const VisualEditor = lazyRetry(() => import("./pages/VisualEditor"));
const ServiceDetails = lazyRetry(() => import("./pages/ServiceDetails"));
const BlogDetails = lazyRetry(() => import("./pages/BlogDetails"));
const BlogTopic = lazyRetry(() => import("./pages/BlogTopic"));
const Admin = lazyRetry(() => import("./pages/Admin"));
const SEOReport = lazyRetry(() => import("./pages/SEOReport"));
const SEOMonitoring = lazyRetry(() => import("./pages/SEOMonitoring"));
const AdvisorInsights = lazyRetry(() => import("./pages/AdvisorInsights"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const SkiSeason = lazyRetry(() => import("./pages/SkiSeason"));
const SummerPrep = lazyRetry(() => import("./pages/SummerPrep"));
// English SEO landing pages (expat / international audience) — prerendered at build time
const EnglishLanding = lazyRetry(() => import("./pages/EnglishLanding"));
const EnglishPrices = lazyRetry(() => import("./pages/EnglishPrices"));

const queryClient = new QueryClient();

// Minimal loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

// Mounts page-view + delegated conversion-click tracking. Must live inside <BrowserRouter>.
const AnalyticsBridge = () => {
  usePageTracking();
  useConversionTracking();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <DomainRedirect />
          <ScrollToTop />
          <AnalyticsBridge />
          <BackToTop />
          <WhatsAppFloat />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/index" element={<Navigate to="/" replace />} />
              <Route path="/index.html" element={<Navigate to="/" replace />} />
              <Route path="/home" element={<Navigate to="/" replace />} />
              <Route path="/sr" element={<Navigate to="/" replace />} />
              {[
                ['/index.php', '/'], ['/en', '/'], ['/ru', '/'],
                ['/kontakt', '/contact'], ['/o-nama', '/about'], ['/usluge', '/services'],
                ['/zakazivanje', '/booking'], ['/cene', '/cenovnik'],
                ['/fizikalna-terapija', '/fizikalna-terapija-novi-beograd'],
                ['/kiropraktika', '/kiropraktika-novi-beograd'],
                ['/limfna-drenaza', '/limfna-drenaza-novi-beograd'],
                ['/services/limfna-drenaza', '/limfna-drenaza-novi-beograd'],
                ['/shockwave-terapija', '/shockwave-terapija-novi-beograd'],
                ['/masaza', '/masaza-novi-beograd'],
                ['/services/masaza', '/masaza-novi-beograd'],
                ['/presoterapija', '/presoterapija-novi-beograd'],
              ].map(([from, to]) => (
                <Route key={from} path={from} element={<Navigate to={to} replace />} />
              ))}
              <Route path="/services" element={<Services />} />
              {/* 301-equivalent redirects: old service URLs → new SEO landings (fix keyword cannibalization) */}
              <Route path="/services/cellulite-removal" element={<Navigate to="/tretman-celulita-novi-beograd" replace />} />
              <Route path="/anticelulit-tretman-beograd" element={<Navigate to="/tretman-celulita-novi-beograd" replace />} />
              <Route path="/services/shockwave-therapy" element={<Navigate to="/shockwave-terapija-novi-beograd" replace />} />
              <Route path="/services/pressotherapy" element={<Navigate to="/presoterapija-novi-beograd" replace />} />
              {/* SEO slug migration: keyword-rich URL + 301-equivalent redirect */}
              <Route path="/services/postural-training" element={<Navigate to="/korektivne-vezbe-za-decu-novi-beograd" replace />} />
              {/* SEO: uklanjanje kanibalizacije — usluge vode na glavne landing stranice */}
              <Route path="/services/fizikalna-terapija" element={<Navigate to="/fizikalna-terapija-novi-beograd" replace />} />
              <Route path="/services/kiropraktika" element={<Navigate to="/kiropraktika-novi-beograd" replace />} />
              <Route path="/services/lymphatic-drainage" element={<Navigate to="/limfna-drenaza-novi-beograd" replace />} />
              <Route path="/services/hernia-preparation" element={<Navigate to="/diskus-hernija-tretman-beograd" replace />} />
              {/* SEO: kratak egzaktan slug za upit "petni trn" */}
              <Route path="/petni-trn-plantarni-fascitis-beograd" element={<Navigate to="/petni-trn" replace />} />
              <Route path="/korektivne-vezbe-za-decu-novi-beograd" element={<ServiceDetails serviceIdOverride="postural-training" />} />
              <Route path="/services/:serviceId" element={<ServiceDetails />} />
              
              
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/booking" element={<Booking />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/teme/:topicSlug" element={<BlogTopic />} />
              <Route path="/blog/:blogId" element={<BlogDetails />} />
              <Route path="/physiotherapy-education-program-europe" element={<ClinicalEducation />} />
              <Route path="/dijagnostika" element={<Diagnostics />} />
              {DIAGNOSTIC_SERVICES.map((d) => (
                <Route
                  key={d.slug}
                  path={`/${d.slug}`}
                  element={<DiagnosticServiceLanding slug={d.slug} />}
                />
              ))}
              <Route path="/kiropraktika-novi-beograd" element={<ChiropracticLanding />} />
              <Route path="/fizikalna-terapija-novi-beograd" element={<PhysioTherapyLanding />} />
              <Route path="/masaza-novi-beograd" element={<MassageLanding />} />
              <Route path="/masaze-beograd" element={<MassageCityLanding variant="beograd" />} />
              <Route path="/masaze-novi-beograd" element={<Navigate to="/masaza-novi-beograd" replace />} />
              <Route path="/laser-terapija-novi-beograd" element={<LaserTherapyLanding />} />
              <Route path="/cenovnik" element={<Pricing />} />
              <Route path="/sta-me-boli" element={<SymptomAdvisor />} />
              <Route path="/pretraga" element={<SiteSearch />} />
              <Route path="/limfna-drenaza-novi-beograd" element={<LymphaticDrainageLanding />} />
              {/* Google Ads final URL — same page, canonical stays on /limfna-drenaza-novi-beograd */}
              <Route path="/limfna-drenaza/novi-beograd" element={<LymphaticDrainageLanding />} />
              <Route path="/presoterapija-novi-beograd" element={<PressotherapyLanding />} />
              <Route path="/shockwave-terapija-novi-beograd" element={<ShockwaveLanding />} />
              <Route path="/shockwave-terapija-beograd" element={<ShockwaveCityLanding />} />
              <Route path="/skidanje-masti-novi-beograd" element={<FatRemovalLanding />} />
              <Route path="/tretman-celulita-novi-beograd" element={<AntiCelluliteLanding />} />

              {/* Diagnosis landing pages — auto-generated from src/data/diagnoses.ts */}
              {DIAGNOSES.map((d) => (
                <Route
                  key={d.slug}
                  path={`/${d.slug}`}
                  element={<DiagnosisLanding slug={d.slug} />}
                />
              ))}
              {/* Belgrade pillar — Faza 6 */}
              <Route path="/fizikalna-terapija-beograd" element={<BelgradePillar />} />
              {/* Location landing pages — auto-generated from src/data/locations.ts */}
              {LOCATIONS.map((l) => (
                <Route
                  key={l.slug}
                  path={`/${l.slug}`}
                  element={<LocationLanding slug={l.slug} />}
                />
              ))}
              {/* Knowledge Center — Faza 5 */}
              <Route path="/baza-znanja" element={<KnowledgeHub />} />
              {KNOWLEDGE.map((k) => (
                <Route
                  key={k.slug}
                  path={`/baza-znanja/${k.slug}`}
                  element={<KnowledgeLanding slug={k.slug} />}
                />
              ))}
              {/* Faza 7 — Author bio stranice (E-E-A-T) */}
              {AUTHORS.map((a) => (
                <Route
                  key={a.slug}
                  path={`/tim/${a.slug}`}
                  element={<AuthorLanding slug={a.slug} />}
                />
              ))}
              {/* Faza 9 — Comparison / Decision stranice */}
              {COMPARISONS.map((c) => (
                <Route
                  key={c.slug}
                  path={`/poredjenje/${c.slug}`}
                  element={<ComparisonLanding slug={c.slug} />}
                />
              ))}
              {/* Faza 10 — Sport hub stranice */}
              {SPORTS.map((s) => (
                <Route
                  key={s.slug}
                  path={`/${s.slug}`}
                  element={<SportLanding slug={s.slug} />}
                />
              ))}
              <Route path="/ski-sezona" element={<SkiSeason />} />
              <Route path="/letnja-priprema" element={<SummerPrep />} />
              {/* English landing pages — src/data/englishLandings.ts (also prerendered by scripts/prerender-en.tsx) */}
              {EN_LANDINGS.map((l) => (
                <Route key={l.slug} path={`/${l.slug}`} element={<EnglishLanding slug={l.slug} />} />
              ))}
              <Route path="/prices" element={<EnglishPrices />} />
              {/* SEO monitoring — dostupno u produkciji za tim */}
              <Route path="/admin/seo-monitoring" element={<SEOMonitoring />} />
              <Route path="/admin/pitanja" element={<AdvisorInsights />} />
              {import.meta.env.DEV && (
                <>
                  <Route path="/visual-editor" element={<VisualEditor />} />
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/admin/seo-report" element={<SEOReport />} />
                </>
              )}
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
